<template>
  <div
    v-if="designId && current && activeSubNavigation !== SubNavigations.Settings"
    ref="kkDesigner"
    :class="{
      'kk-designer': true,
      'kk-content-focused': contentFocused,
    }"
    @click="setScope"
  >
    <busy-overlay :visible="action.status[designId] == 'loading' || isSheetLoading" />
    <add-table-dialog v-if="showDialog" />
    <add-named-range-dialog v-if="showAddNamedRangeDialog" />
    <named-manager-dialog v-if="showNameMangerDialog" />
    <template v-if="action.status[designId] != 'loading'">
      <design-header :design-id="designId" />

      <validation-errors :title="$t('processDesigner.fixValidationIssues')" :errors="action.error[designId]" />

      <div class="kk-designer__design">
        <virtual-scroll v-if="!activityBeingLinked && !isSheetLoading"></virtual-scroll>
        <no-object-in-design
          v-if="!activityBeingLinked && players.length < 1 && activities.length < 1 && current.type !== 'grid'"
        />
        <cell-context-menu v-if="activeContextMenu.type === 'cell'" />
      </div>
    </template>
    <custom-cursor v-if="showCursor"></custom-cursor>
  </div>
  <settings v-else-if="activeSubNavigation === SubNavigations.Settings" />
  <no-design-selected v-else />
</template>

<script>
import ValidationErrors from "../ValidationErrors.vue";
import DesignHeader from "./DesignHeader.vue";
import NoDesignSelected from "./NoDesignSelected.vue";
import NoObjectInDesign from "./NoObjectInDesign.vue";
import CustomCursor from "./body/tracks/custom-cursor/CustomCursor.vue";
import { CustomCursorTypes, HotkeyScopes, Positions, SubNavigations } from "../../utils/constants";
import DesignerMixin from "./DesignerMixin";
import hotkeys from "hotkeys-js";
import Settings from "../settings/Settings.vue";
import BusyOverlay from "../commons/BusyOverlay.vue";
import CellContextMenu from "./cell/cell-context-menu/CellContextMenu.vue";
import VirtualScroll from "./VirtualScroll.vue";
import AddTableDialog from "./structure-table/AddTableDialog.vue";
import AddNamedRangeDialog from "./structure-table/AddNamedRangeDialog.vue";
import NamedManagerDialog from "./structure-table/NameManagerDialog.vue";
export default {
  components: {
    BusyOverlay,
    DesignHeader,
    ValidationErrors,
    NoDesignSelected,
    NoObjectInDesign,
    CustomCursor,
    Settings,
    CellContextMenu,
    VirtualScroll,
    AddTableDialog,
    NamedManagerDialog,
    AddNamedRangeDialog,
  },
  mixins: [DesignerMixin],
  props: ["designId"],
  data() {
    return {
      scrollEvent: null,
      showVerticalShadow: false,
      hideTimer: null,
      SubNavigations,
      isAltPressed: false, // Track if Alt is pressed alone
    };
  },
  computed: {
    isSheetLoading() {
      return this.$store.state.sheets.isLoading;
    },
    activeContextMenu() {
      return this.$store.state.activeContextMenu;
    },
    action() {
      return this.$store.state.cells.action;
    },
    contentFocused() {
      return this.pdesign.contentFocused;
    },
    activityBeingLinked() {
      return this.pdesign.activityBeingLinked;
    },
    showCursor() {
      return (
        this.$store.state.cells.arrow.activeCursor != CustomCursorTypes.Default ||
        this.$store.state.customFlow.newArrow.activeCursor != CustomCursorTypes.Default
      );
    },
    dropIndicator() {
      return this.$store.state.cells.dropIndicator;
    },
    activeSubNavigation() {
      return this.$store.state.preferences.allPreferences?.activeSubNavigation;
    },
    showDialog() {
      return this.$store.state.table.showAddTableDialog;
    },
    showNameMangerDialog() {
      return this.$store.state.table.nameManagerVisibility;
    },
    showAddNamedRangeDialog() {
      return this.$store.state.table.addNamedRangeVisibility;
    },
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleKeyDown);
    document.removeEventListener("keyup", this.handleKeyUp);
  },
  mounted() {
    window.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("keyup", this.handleKeyUp);
  },
  methods: {
    getPosition(e, sum, size) {
      if (sum - e.clientX > 3 * (size / 4)) return [Positions.Before, sum - size - 3.2];
      else if (sum - e.clientX < size / 4) return [Positions.After, sum + 3.2];
      else return [Positions.Center, sum - size / 2];
    },
    setScope() {
      this.$store.dispatch("players/resetActiveItem");
      const currentScope = hotkeys.getScope();
      if (
        Object.values(HotkeyScopes.Sidebar).some((scope) => currentScope === scope) ||
        currentScope === HotkeyScopes.Search
      ) {
        this.$store.dispatch("setScope", HotkeyScopes.ProcessDesigner);
      }
    },

    handleKeyDown(event) {
      if (event.key === "Alt") {
        this.isAltPressed = true; // Alt is pressed
      } else {
        this.isAltPressed = false; // Another key is pressed along with Alt
      }
    },
    handleKeyUp(event) {
      event.preventDefault();

      // Close Add Table Dialog on Escape
      if (event.key === "Escape" && this.showDialog) {
        this.$store.dispatch("table/setShowAddTableDialog", false);
      }

      // If Alt is released and no other key was pressed, toggle Smart Search
      if (event.key === "Alt" && this.isAltPressed) {
        this.isAltPressed = false; // Reset state
        const showSmartSearch = this.$store.state.pdesign.isSmartSearchBoxVisible;
        this.$store.dispatch("pdesign/setSmartSearchBoxVisibility", !showSmartSearch);
      }
    },
  },
};
</script>

<style lang="less">
@import "../../assets/styles/variables";

.kk-designer,
.kk-designer__design {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.kk-designer {
  align-items: flex-start;
  width: 0; // just a hack
  height: calc(100vh - 4rem);
  border-left: 1.6rem solid transparent;
  padding-bottom: 2rem;

  &__design {
    background-color: @kk-color-designer-background;
    border-radius: 10px;
    width: 100%;
    margin-right: 20px;
    margin-bottom: 1rem;
    padding-bottom: 45px;
    height: 100%;
  }

  &.kk-content-focused {
    margin-left: 0;
    height: calc(100vh - 0.5rem);
  }

  .validation-errors {
    margin-left: 1.5rem;
    margin-bottom: 0.5rem;
  }
  &__cell {
    &-theme {
      &-pink {
        color: #333;
        overflow: hidden;
        font-size: 14px;
        position: relative;
        background-color: @kk-color-gold;
        align-content: center;
      }
      &-blue {
        color: rgb(255, 251, 251);
        background-color: rgb(114, 165, 226);
        align-content: center;
      }
      &-gray,
      &-rightAlign {
        color: #aaa;
        font-size: 10px;
        background-color: @kk-color-designer-background;
      }
      &-rightAlign {
        align-content: center;
        justify-content: end;
        & textarea {
          text-align: right;
        }
      }
      &-gray {
        align-content: end;
      }
      &-lightblue {
        background-color: @kk-color-light-blue;
        font-size: 14px;
        color: #333;
        text-align: center;
        align-content: center;
        & textarea {
          text-align: center;
        }
      }
      &-cornerLightBlue {
        background-color: @kk-color-light-blue;
        font-size: 14px;
        color: #333;
      }
      &-white {
        color: black;
        background-color: @kk-color-white;
      }
      &-number,
      &-letter {
        background-color: @kk-coordinates-bg;
        color: #d3d3d3;
        font-size: 0.85rem;
        font-weight: 400;
        text-align: center;
        cursor: pointer;
        align-content: center;
      }
      &-number {
        justify-content: end;
        padding-right: 4px;
      }
      &-signIcon {
        justify-content: center;
        background-color: @kk-color-light-blue;
        align-content: center;
      }
    }
  }
}
</style>
